import { Fragment, type ReactNode, type ThHTMLAttributes } from "react";
import { cn } from "./cn";
import { Ikon } from "./Ikon";

export interface TabloSutunu<T> {
  /** Başlık hücresi metni */
  baslik: string;
  /** Hücre içeriği */
  hucre: (satir: T) => ReactNode;
  /**
   * DAR EKRANDA da gösterilir.
   *
   * Telefonda satırlar kart gibi diziliyor ve her alan alt alta
   * geliyor. Bütün sütunları göstermek kartı uzun bir etiket listesine
   * çevirirdi; işaretsiz sütunlar dar ekranda gizleniyor.
   */
  mobil?: boolean;
  /** Sayısal sütunlar sağa yaslanır — rakamlar alt alta hizalansın */
  sayisal?: boolean;
  /**
   * DAR EKRANDA etiket yazılmaz ("İşlem:" gibi) — UI/UX Dalga 1 (M-03).
   *
   * Kartta her hücre kendi sütun başlığını `::before` ile yazıyor; yalnız
   * düğme taşıyan bir sütunda "İşlem:" tek başına duran, hiçbir şey
   * anlatmayan bir etiket oluyordu. Başlık ekran okuyucuya yine
   * `<th>`dan geliyor; bu yalnız görünür kart etiketini kaldırır.
   * Varsayılan `false`: mevcut sütunlar değişmez.
   */
  etiketGizli?: boolean;
  className?: string;
}

export interface TabloProps<T> {
  sutunlar: readonly TabloSutunu<T>[];
  satirlar: readonly T[];
  anahtar: (satir: T) => string;
  /** Satır eylemi — verilmezse eylem sütunu hiç çizilmez */
  onSatir?: (satir: T) => void;
  /** Satır eylemi düğmesinin metni (erişilebilir ad buradan gelir) */
  eylemEtiketi?: string;
  /** Kart görünümünde başlık olarak kullanılacak sütunun dizini */
  kartBasligi?: number;
  /** Satırın altına ek içerik (açılır ayrıntı) */
  altSatir?: (satir: T) => ReactNode;
  /**
   * Satır SEÇİLİ mi?
   *
   * Çizim paketi tablo satırına beş hâl veriyor — varsayılan,
   * üzerinde, seçili, odak, kapalı — ve bileşen bunlardan yalnızca
   * ikisini taşıyordu. Seçili hâl bir ekran özelliği değil: sekiz
   * ekranda "seçili öğrenci" küresel bir ön koşul ve hangi satırda
   * olduğunu göstermek tablonun işi.
   */
  secili?: (satir: T) => boolean;
  /**
   * Satır KAPALI mı — görünür ama sana açık değil.
   *
   * Paketin üç sert kuralından biri: "Yetkisiz öğe genelde gizlenir;
   * üç istisnada varlığın kendisi bilgi taşır." Kapsamı dışındaki bir
   * öğrenciyi listeden tamamen silmek, koça "böyle biri yok" demek
   * olur — oysa var, sadece ona kapalı. Satır duruyor, soluyor,
   * eylem düğmesinin yerinde kilit var.
   */
  kapali?: (satir: T) => boolean;
  /** Kapalı satırın ekran okuyucuya verilen gerekçesi */
  kapaliEtiketi?: string;
  /**
   * Başlık satırının görünümü. `degrade`: UNİKOÇ program tablosundaki mor → pembe
   * başlık (sürüm 9a). Varsayılan `sade`: mevcut tablolar değişmez.
   */
  baslikTuru?: "sade" | "degrade";
  /**
   * Satır zemini — TAM sınıf adı döner (Tailwind birleştirilmiş adı tarayamaz): UNİKOÇ program
   * tablosunda satır gün rengiyle boyanıyor (`HAFTA_GUNLERI[i].zemin`, ör. `bg-gun-pzt`).
   * Seçili satır yine `bg-marka-50` alır (seçim durumu gün renginden önemli).
   */
  satirZemini?: (satir: T) => string | undefined;
  /**
   * Satır DURUM rengi (UNİKOÇ: tamamlandı yeşil, yarım sarı, yapılmadı kırmızı zemin). Renk tek
   * sinyal değil: ekran durumu ayrıca metinle de yazmalı (`DurumRozeti`). `satirZemini` verildiyse o kazanır.
   */
  satirDurumu?: (satir: T) => "basarili" | "uyari" | "hata" | undefined;
}

const SATIR_DURUM_ZEMINI = {
  basarili: "bg-tamam-zemin",
  uyari: "bg-bekliyor-zemin",
  hata: "bg-eksik-zemin",
} as const;

/**
 * UNİKOÇ DEGRADE TABLO BAŞLIĞI (sürüm 9a; tarama-2 §2.3, EditSchedule ekranı)
 *
 * Eski panelde degrade HER HÜCREDE ayrı başlıyor (ölçüldü: her sütunun solu mor
 * #8544E0 civarı, sağı pembe) ve hücreler arasında ince beyaz çizgi var. Tek
 * degradeyi bütün satıra yaymak bu görünümü vermiyor; o yüzden hücre sınıfı.
 * Elle kurulan tablolar (program düzenleyici) `DegradeBaslikHucresi`ni kullanır.
 *
 * ⚠️ Beyaz yazı degradenin pembe ucunda 2,90 — AA altında (tasarım birebir).
 */
export const DEGRADE_BASLIK_SINIFI = cn(
  "bg-(image:--gradyan-unk-tablo) text-notr-0 font-semibold",
  "border-notr-0 border-r last:border-r-0",
);

export function DegradeBaslikHucresi({
  children,
  className,
  ...rest
}: ThHTMLAttributes<HTMLTableCellElement> & { children?: ReactNode }) {
  return (
    <th scope="col" {...rest} className={cn(DEGRADE_BASLIK_SINIFI, "px-3 py-2.5", className)}>
      {children}
    </th>
  );
}

/**
 * TABLO — geniş ekranda yoğun liste, dar ekranda kart
 *
 * ================================================================
 * NEDEN GEREKTİ — ÖLÇÜLDÜ
 * ================================================================
 * 06.08.2026: panelin 27 ekranında `<table>` sayısı SIFIRDI; her liste
 * kart yığınıydı. Öğrenciler ekranında bir satır **126px** yer
 * kaplıyor ve 1280×720'de 21 öğrencinin yalnızca **5'i** görünüyordu.
 *
 * Kart, bir satırda üç dört alan varken doğru seçim. Ama koç sabah
 * listeye TARAMA yapmak için bakıyor: kim hangi sınıfta, kimin durumu
 * ne. Tarama için alanların hizalı olması gerekiyor — kartta her alan
 * kendi satırında ve göz her kartta baştan hizalanıyordu.
 *
 * ================================================================
 * TEK AĞAÇ, SAF CSS — İKİ YOL DA DENENDİ VE ELENDİ
 * ================================================================
 * 07.08.2026'da iki yaklaşım denendi, ikisi de bırakıldı:
 *
 *   1) İKİ AĞAÇ (tablo + kart listesi, biri `hidden md:block`).
 *      Çalışıyordu ama **var olan 15 ekran testini düşürdü**: jsdom'da
 *      Tailwind çalışmadığı için iki ağaç da DOM'da duruyor ve her
 *      sorgu iki sonuç buluyordu. Ayrıca DOM düğümlerini ikiye
 *      katlıyordu.
 *
 *   2) JS ile seçim (`matchMedia` + `useState`). Tek ağaç çiziyordu,
 *      testler geçiyordu — ama yeniden boyutlandırmada görünümün
 *      GERÇEKTEN değiştiğini doğrulayamadım: tarayıcı otomasyonu
 *      görüntü alanını programla değiştirdiğinde ne `matchMedia`nın
 *      `change` olayı ne de `resize` ateşleniyor. Kanıtlanamayan bir
 *      mekanizmaya güvenmek, bu projedeki "düşemeyen kontrol kontrol
 *      değildir" kuralının tersi olurdu.
 *
 * Kalan yol: TEK ağaç (her zaman `<table>`) + saf CSS. Dar ekranda
 * tablo `display:block` ile kart gibi diziliyor. Tarayıcı bunu kendi
 * yapıyor — dinlenecek bir olay, doğrulanacak bir varsayım yok.
 *
 * ================================================================
 * SATIRIN TAMAMI TIKLANABİLİR DEĞİL
 * ================================================================
 * `<tr onClick>` ekran okuyucuda düğme olarak duyurulmuyor ve
 * klavyeyle ulaşılamıyor. Her satırın kendi EYLEM DÜĞMESİ var ve
 * düğmenin erişilebilir adı satırın kimliğini taşıyor ("Ayşe Yılmaz
 * — aç"), yoksa ekran okuyucu yirmi tane "Aç" duyururdu.
 */
export function Tablo<T>({
  sutunlar,
  satirlar,
  anahtar,
  onSatir,
  eylemEtiketi = "Aç",
  kartBasligi = 0,
  altSatir,
  secili,
  kapali,
  kapaliEtiketi = "Kapsamınız dışında",
  baslikTuru = "sade",
  satirZemini,
  satirDurumu,
}: TabloProps<T>) {
  const degrade = baslikTuru === "degrade";
  const baslikSutunu = sutunlar[kartBasligi];
  const altSatirIcerigi = (satir: T): ReactNode => altSatir?.(satir) ?? null;

  /** Kart başlığının metni — eylem düğmesinin erişilebilir adı için. */
  const satirAdi = (satir: T): string => {
    const d = baslikSutunu?.hucre(satir);
    return typeof d === "string" || typeof d === "number" ? String(d) : anahtar(satir);
  };

  return (
    <div data-ui="tablo-kap" className="overflow-x-auto">
      {/*
        `max-md:block` zinciri: dar ekranda tablo yapısı çözülüp
        satırlar kart gibi diziliyor. Yatay kaydırma OLUŞMUYOR —
        telefonda yana kayan tablo okunmuyor.
      */}
      <table
        data-ui="tablo"
        data-baslik-turu={degrade ? "degrade" : undefined}
        className="w-full border-collapse text-sm max-md:block"
      >
        {/*
          Dar ekranda başlık satırı gizli: her hücre kendi etiketini
          `data-baslik` ile taşıyor ve `::before` ile yazıyor.
        */}
        <thead className="max-md:sr-only">
          <tr className="border-notr-200 border-b text-left">
            {sutunlar.map((s) => (
              <th
                key={s.baslik}
                scope="col"
                className={cn(
                  "px-3 py-2",
                  degrade ? DEGRADE_BASLIK_SINIFI : "text-notr-600 font-medium",
                  s.sayisal && "text-right",
                )}
              >
                {s.baslik}
              </th>
            ))}
            {onSatir && (
              // Baslik metni gorunmuyor ama sutun DUYURULUYOR: bos bir
              // `th` ekran okuyucuda "sutun 5" diye gecer ve ne oldugu
              // anlasilmaz.
              <th scope="col" className={cn("px-3 py-2", degrade && DEGRADE_BASLIK_SINIFI)}>
                <span className="sr-only">İşlem</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="max-md:block">
          {satirlar.map((satir) => {
            const secildi = secili?.(satir) ?? false;
            const kapandi = kapali?.(satir) ?? false;
            const durum = satirDurumu?.(satir);
            const ozelZemin =
              satirZemini?.(satir) ?? (durum ? SATIR_DURUM_ZEMINI[durum] : undefined);
            return (
              <Fragment key={anahtar(satir)}>
                <tr
                  className={cn(
                    "group border-notr-100 border-b",
                    /*
                    GECIS GUNDELIK KOVADA. Satir vurgusu gunde yuzlerce
                    kez oluyor; `sure-normal` burada beklemeye donusur.
                  */
                    "ease-cikis transition-colors duration-(--sure-anlik)",
                    "max-md:rounded-kart max-md:border-notr-200 max-md:mb-2 max-md:block max-md:border max-md:p-3",
                    /*
                    ODAK SATIRIN KENDISINE DEGIL ICINDEKI OGEYE GELIYOR.
                    `<tr>` odaklanamaz; klavyeyle gezen kullanici satirin
                    EYLEM DUGMESINE ulasiyor. `focus-within` o odagi
                    satira tasiyor — yoksa 12 sutunlu bir satirda kucuk
                    bir dugmenin halkasi, "hangi satirdayim" sorusunu
                    cevaplamiyor.
                  */
                    "focus-within:outline-marka-600 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2",
                    // Kapali satir vurgulanmiyor: vurgu "bunu secebilirsin"
                    // demek ve o soz burada tutulamaz.
                    !kapandi && !secildi && !ozelZemin && "hover:bg-notr-100",
                    !kapandi && !secildi && ozelZemin && [ozelZemin, "hover:brightness-[0.97]"],
                    secildi && "bg-marka-50",
                    kapandi && "text-notr-600",
                  )}
                >
                  {sutunlar.map((s, i) => {
                    const icerik = s.hucre(satir);
                    return (
                      <td
                        key={s.baslik}
                        data-baslik={s.baslik}
                        className={cn(
                          "text-notr-800 px-3 py-2",
                          kapandi && "text-notr-600",
                          /*
                        SOL ISARET — "hangi satirdayim?"
                        Zemin degisimi tek basina yetersiz: goz 12 sutunlu
                        bir satirda kendini kaybediyor, cunku zemin farki
                        satirin ORTASINDA da aynidir ve konum bildirmez.
                        Sol kenardaki 2px cizgi konumu sabitliyor.

                        `border-l`, `box-shadow` DEGIL: `border-collapse:
                        collapse` altinda `<tr>`/`<td>` golgesinin cizilip
                        cizilmedigi tarayiciya gore degisiyor. Kenarlik
                        her yerde ciziliyor ve yerini de kaplamiyor —
                        saydam hali ayni genislikte duruyor, yani satir
                        vurgulaninca metin KAYMIYOR.
                      */
                          i === 0 && "border-l-2 border-l-transparent max-md:border-l-0",
                          i === 0 && !kapandi && !secildi && "group-hover:border-l-marka-400",
                          i === 0 && secildi && "border-l-marka-600",
                          s.sayisal && "text-right tabular-nums",
                          // Dar ekranda: blok diz, etiketini yaz.
                          "max-md:block max-md:px-0 max-md:py-0.5 max-md:text-left",
                          s.etiketGizli
                            ? "max-md:before:content-none"
                            : "max-md:before:text-notr-600 max-md:before:mr-1 max-md:before:content-[attr(data-baslik)_':']",
                          // Kart basligi etiketsiz ve daha belirgin.
                          i === kartBasligi &&
                            "max-md:text-notr-900 max-md:text-sm max-md:font-medium max-md:before:content-none",
                          // İşaretsiz sütunlar dar ekranda GİZLİ.
                          !s.mobil && i !== kartBasligi && "max-md:hidden",
                          /*
                          BOS HUCRE DAR EKRANDA GIZLI — UI/UX Dalga 1 (M-03).
                          Etiket `::before` ile yaziliyor; icerik yokken kartta
                          tek basina "İşlem:" ya da "Uyarı:" kaliyordu — okuyan
                          bir seyin eksik oldugunu saniyor. Genis ekranda hucre
                          duruyor: sutun hizasi bozulmasin.
                        */
                          bosMu(icerik) && "max-md:hidden",
                          s.className,
                        )}
                      >
                        {icerik}
                      </td>
                    );
                  })}
                  {onSatir && (
                    <td className="px-3 py-2 text-right max-md:block max-md:px-0 max-md:pt-2 max-md:text-left">
                      {kapandi ? (
                        /*
                        KAPALI SATIRDA DEVRE DISI DUGME DEGIL, KILIT.
                        Devre disi bir dugme "simdi olmaz, birazdan
                        olabilir" der; burada anlatilmak istenen "bu sana
                        hic acilmayacak". Kilit tiklanamaz bir isaret ve
                        sebebi `sr-only` metinle duyuruluyor — ikon tek
                        basina anlam tasimaz.
                      */
                        <span className="min-h-dokunma text-notr-600 inline-flex items-center justify-end px-2">
                          <Ikon ad="kilit" boyut="kucuk" />
                          <span className="sr-only">
                            {satirAdi(satir)} — {kapaliEtiketi}
                          </span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onSatir(satir)}
                          className={cn(
                            "min-h-dokunma rounded-alan text-marka-700 px-2 text-sm",
                            "ease-cikis hover:bg-marka-50 transition-colors duration-(--sure-anlik)",
                            "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                          )}
                        >
                          {eylemEtiketi}
                          {/*
                          Satirin kimligi: yirmi tane "Ac" duyurmak,
                          hicbirini ayirt edilebilir yapmiyor.
                        */}
                          <span className="sr-only"> — {satirAdi(satir)}</span>
                        </button>
                      )}
                    </td>
                  )}
                </tr>

                {/*
                BOS `<tr>` CIZILMIYOR. Ilk yazimda `altSatir` varsa her
                satirin altina bir satir daha koyuyordum; icerik `null`
                olsa bile. 21 ogrencilik listede 21 bos satir demek —
                gorunmuyor ama ekran okuyucu onlari da sayiyor.
              */}
                {altSatirIcerigi(satir) !== null && (
                  <tr className="max-md:block">
                    <td colSpan={sutunlar.length + (onSatir ? 1 : 0)} className="p-0 max-md:block">
                      {altSatirIcerigi(satir)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Hucre cizilecek bir sey tasimiyor mu — React'in hic cizmedigi degerler. */
function bosMu(d: ReactNode): boolean {
  return d === null || d === undefined || d === false || d === "";
}
